import { Stack } from '@startuptools/ui/Stack';
import {
  OptionsRegistrationsDocument,
  useOptionsRegistrationsSuspenseQuery,
} from '../../../OptionsRegistration.graphql';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { OptionsRegistration } from './OptionsRegistration';
import { GqlWarrantProgramKind } from '../../../../../graphql/base-types.graphql';
import { Card } from '@startuptools/ui/Card';
import { Trans } from '@lingui/react';
import { Typography } from '@startuptools/ui/Typography';
import { Button } from '@startuptools/ui/Button';
import { Box } from '@startuptools/ui/Box';
import { useCreateOptionsRegistrationMutation } from '../../../../../graphql/react-operations';
import { PromptDialog } from '../../../../../components/simple-dialogs/PromptDialog';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { Input } from '@startuptools/ui/Input';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useState } from 'react';
import { captureException } from '@sentry/react';
import { genericErrorToast } from '@startuptools/ui/toast/use-toast';
import { orderBy } from 'lodash-es';

type FormValues = {
  description: string;
};

export const OptionsRegistrations = ({ warrantProgramId }: { warrantProgramId: string }) => {
  const companyId = useCompanyId();
  const [showCreate, setShowCreate] = useState(false);
  const {
    data: { optionsRegistrations, warrantProgram },
  } = useOptionsRegistrationsSuspenseQuery({ variables: { companyId, warrantProgramId } });
  const [doSubmit, { loading }] = useCreateOptionsRegistrationMutation({
    refetchQueries: [OptionsRegistrationsDocument],
    awaitRefetchQueries: true,
  });

  const { register, handleSubmit } = useForm<FormValues>();

  const onSubmit: SubmitHandler<FormValues> = async values => {
    try {
      await doSubmit({ variables: { companyId, input: { warrantProgramId, description: values.description } } });
      setShowCreate(false);
    } catch (e) {
      captureException(e);
      genericErrorToast();
    }
  };

  return (
    <>
      <Stack gap={1}>
        {orderBy(optionsRegistrations, or => or.createdAt, 'asc').map(or => (
          <OptionsRegistration key={or.id} orId={or.id} warrantProgramId={or.warrantProgramId} />
        ))}

        {warrantProgram.warrantKind === GqlWarrantProgramKind.Warrant && (
          <Card sx={{ alignItems: 'center' }}>
            {optionsRegistrations.length === 0 && (
              <Typography>
                <Trans id="The decision is now done. In this step you create the registration" />
              </Typography>
            )}

            <Box>
              <Button onClick={() => setShowCreate(true)}>
                <Trans id="Create New Registration" />
              </Button>
            </Box>
          </Card>
        )}
      </Stack>
      {showCreate && (
        <PromptDialog
          loading={loading}
          onCancel={() => setShowCreate(false)}
          onSubmit={handleSubmit(onSubmit)}
          title={<Trans id="New registration of option subscription" />}
        >
          <FormControl>
            <FormLabel>
              <Trans id="Description" />
            </FormLabel>
            <Input {...register('description')} />
          </FormControl>
        </PromptDialog>
      )}
    </>
  );
};
